import * as Tooltip from '@radix-ui/react-tooltip';
import {
  getPricingIntegrationIcon,
  INCLUDED_PRICING_INTEGRATIONS,
} from '@common/config/pricingCatalog';
import clsx from 'clsx';

export const INCLUDED_INTEGRATION_NAMES = INCLUDED_PRICING_INTEGRATIONS;

interface IncludedIntegrationsProps {
  label: string;
  planName?: string;
}

const getIntegrationInitials = (integration: string) => {
  const words = integration.split(/[.\s]+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
};

const getIntegrationImageClass = (integration: string) => {
  const baseClass = 'h-[18px] w-[18px] object-contain';

  const scaleClass = {
    Wix: 'scale-[1.3]',
    Magento: 'scale-[1.25]',
    WooCommerce: 'scale-[1.1]',
    PrestaShop: 'scale-[1.1]',
    'Google Ads': 'scale-[1.35]',
    'Google Analytics': 'scale-[1.6]',
    Instagram: 'scale-[1.45]',
  }[integration];

  return clsx(baseClass, scaleClass);
};

const IncludedIntegrations = ({ label, planName }: IncludedIntegrationsProps) => {
  const isSolo = planName === 'Solo';
  const integrations = INCLUDED_INTEGRATION_NAMES.filter(
    (integration) => !(isSolo && (integration === 'Slack' || integration === 'Feishu'))
  );

  return (
    <li className="mt-1 py-0.5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[13px] leading-none text-[#4F4C47]">
          ✓
        </span>
        <span className="text-sm font-normal leading-relaxed text-[#302E2A]">{label}</span>
      </div>
      <div className="mt-2 pl-7">
        <Tooltip.Provider delayDuration={120}>
          <div className="flex flex-wrap items-center gap-1">
            {integrations.map((integration) => (
              <Tooltip.Root key={integration}>
                <Tooltip.Trigger asChild>
                <span
                  aria-label={integration}
                  className={clsx(
                    'flex h-[26px] w-[26px] shrink-0 items-center justify-center border-0 bg-transparent shadow-none ring-0',
                    'outline-none transition-transform duration-200 hover:-translate-y-0.5',
                    'focus-visible:ring-1 focus-visible:ring-[#8A93B2] focus-visible:ring-offset-1',
                  )}
                  role="img"
                  tabIndex={0}
                >
                  <img
                    alt=""
                    className={getIntegrationImageClass(integration)}
                    loading="lazy"
                    src={getPricingIntegrationIcon(integration)}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                      event.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <span aria-hidden className="hidden text-[7px] font-semibold text-[#666F8D]">
                    {getIntegrationInitials(integration)}
                  </span>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  avoidCollisions
                  className="z-[10000] rounded-lg bg-[#1A162F] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg"
                  collisionPadding={8}
                  side="top"
                  sideOffset={6}
                >
                  {integration}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ))}
        </div>
      </Tooltip.Provider>
    </div>
  </li>
  );
};

export default IncludedIntegrations;
